import type { ItemFieldRow } from './item-field-reader';
import { createEnchantmentTemplateBuilder } from './enchantment-template';
import type { EnchantmentTemplateReaders, EnchantmentTemplateMemoEntry } from './enchantment-template';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';
import { createLayerEntriesReader } from './layer-entries-reader';
import { readNodeItemId, readOverrideEntryAbilityId } from './layer-cell-readers';
import { phpJsonTruthy } from './php-json-values';

export interface EnchantmentTemplateDependencies {
    catalogRows: ProviderCatalogRows;
    abilityTypes: readonly string[];
    dynamicLayers: readonly string[];
    spellCosts: readonly number[];
    aggregationStrategy: (name: string) => string;
    templateRows: EnchantmentTemplateReaders['templateRows'];
    memberRows: EnchantmentTemplateReaders['memberRows'];
    fullItem: EnchantmentTemplateReaders['fullItem'];
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    memoEnabled?: boolean;
    initialMemo?: Readonly<Record<string, EnchantmentTemplateMemoEntry | null>>;
    trace?: (event: readonly unknown[]) => void;
}
/** Shared scalar/list/provider/inventory context; only template/member query rows
 * enter the enchantment query memo. Full member items remain explicit inputs.
 */
export function createEnchantmentCompilationContext(deps: EnchantmentTemplateDependencies): {
    buildTemplate: (id: unknown, includePassiveLayers?: unknown) => ItemFieldRow | null;
    layerValue: EnchantmentTemplateReaders['layerValue'];
    layerEntries: EnchantmentTemplateReaders['layerEntries'];
    hasLayer: EnchantmentTemplateReaders['hasLayer'];
} {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId,
        resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); } });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    const lists = createLayerEntriesReader({ nodeItemId, inventoryContext, providers, abilityTypes: deps.abilityTypes,
        aggregationStrategy: name => { const strategy = deps.aggregationStrategy(name); emit(['strategy', name, strategy]); return strategy; },
        overrideEntryAbilityId: entry => { const id = readOverrideEntryAbilityId(entry); emit(['override', id]); return id; } });
    const hasLayer: EnchantmentTemplateReaders['hasLayer'] = (row, name) => scalar.read(row, name) !== null || phpJsonTruthy(lists.read(row, name));
    const buildTemplate = createEnchantmentTemplateBuilder({ dynamicLayers: deps.dynamicLayers, spellCosts: deps.spellCosts,
        templateRows: id => { emit(['template-rows', id]); return deps.templateRows(id); },
        memberRows: id => { emit(['member-rows', id]); return deps.memberRows(id); },
        fullItem: id => { emit(['full-item', id]); return deps.fullItem(id); },
        layerValue: scalar.read, layerEntries: lists.read,
        hasLayer,
        unresolvedMember: () => emit(['warning', 'unresolved-member']),
    }, deps.memoEnabled, deps.initialMemo);
    return { buildTemplate, layerValue: scalar.read, layerEntries: lists.read, hasLayer };
}
export function createEnchantmentTemplateCompiler(deps: EnchantmentTemplateDependencies):
    (id: unknown, includePassiveLayers?: unknown) => ItemFieldRow | null {
    return createEnchantmentCompilationContext(deps).buildTemplate;
}
